<script setup>
import { ref } from 'vue'
import { PhCaretRight, PhStudent } from '@phosphor-icons/vue'

import Modal from '@pages/Shared/Modal.vue'
import { formatDate, capitalize } from '@/helpers.js'
import { usePromotion } from '@pages/Composables/usePromotion.js'

const props = defineProps({
    modelValue: { type: Boolean, required: true },
    sections:   { type: Array, required: true },
})

const emit = defineEmits(['update:modelValue'])

const { getSectionClasses } = usePromotion(props.sections)

const promotion = ref(null)
const expandedClassId = ref(null)

/**
 * Safe JSON parser (prevents repeated try/catch everywhere)
 */
const safeJson = (val, fallback = {}) => {
    if (!val) return fallback
    if (typeof val === 'object') return val
    try {
        return JSON.parse(val) ?? fallback
    } catch {
        return fallback
    }
}

/**
 * Stable subject resolver (FIXED: was previously scoped inside open)
 */
const resolveSubjectName = (cr, subjectId, sectionId) => {
    const classes = getSectionClasses(sectionId)
    const cls = classes.find(c => c.id === cr.from_class_room_id)

    return cls?.subjects?.find(s => s.id === subjectId)?.name ?? '—'
}

const open = (raw) => {
    const sectionId  = raw.section?.id ?? raw.section_id
    const classOrder = getSectionClasses(sectionId).map(c => c.id)

    const sortedClasses = [...(raw.classes ?? [])].sort((a, b) => {
        const ai = classOrder.indexOf(a.from_class_room_id)
        const bi = classOrder.indexOf(b.from_class_room_id)
        return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi)
    })

    promotion.value = {
        ...raw,
        summary: {
            total: (raw.promoted_count ?? 0)
                + (raw.repeated_count ?? 0)
                + (raw.graduated_count ?? 0),
            promoted:  raw.promoted_count ?? 0,
            repeated:  raw.repeated_count ?? 0,
            graduated: raw.graduated_count ?? 0,
        },
        class_rooms: sortedClasses.map(cr => {
            const rulesJson = safeJson(cr.rules_json)

            const students = (cr.students ?? []).map(ps => {
                const resultJson = safeJson(ps.rules_result_json)
                const subjectRules = rulesJson?.subject_rules ?? []

                return {
                    id: ps.student?.id,
                    name: [ps.student?.first_name, ps.student?.last_name, ps.student?.other_name]
                        .filter(Boolean)
                        .join(' '),
                    admission_no: ps.student?.id,
                    from_class: ps.from_class_room,
                    to_class: ps.to_class_room,
                    average_score: ps.average_score,
                    status: ps.status,

                    subject_scores: subjectRules.map(sr => {
                        const score = resultJson?.subject_scores?.[sr.subject_id]

                        return {
                            name: resolveSubjectName(cr, sr.subject_id, sectionId),
                            score,
                            min_score: sr.min_score,
                            passed: score != null ? score >= sr.min_score : null,
                        }
                    }),
                }
            }).sort((a, b) => a.name.localeCompare(b.name))

            return {
                ...cr,
                from_class: cr.from_class_room,
                to_class: cr.to_class_room,
                rules_json: rulesJson,
                summary: {
                    promoted:  cr.students?.filter(s => s.status === 'promoted').length ?? 0,
                    repeated:  cr.students?.filter(s => s.status === 'repeated').length ?? 0,
                    graduated: cr.students?.filter(s => s.status === 'graduated').length ?? 0,
                },
                students,
            }
        }),
    }

    expandedClassId.value = promotion.value.class_rooms[0]?.id ?? null
    emit('update:modelValue', true)
}

const close = () => {
    emit('update:modelValue', false)
    promotion.value = null
    expandedClassId.value = null
}

const toggleClassDrawer = (id) => {
    expandedClassId.value = expandedClassId.value === id ? null : id
}

const statusClass = (status) => {
    if (status === 'promoted')  return 'bg-green-50 text-green-700 border-green-100'
    if (status === 'graduated') return 'bg-primary-50 text-primary border-primary-100'
    return 'bg-orange-50 text-orange-600 border-orange-100'
}

const statusLabel = (status) => {
    if (status === 'promoted')  return 'Promoted'
    if (status === 'graduated') return 'Graduated'
    return 'Repeating'
}

defineExpose({ open })
</script>

<template>
    <Modal :model-value="modelValue" title="Promotion Details" size="large" @close="close">
        <div v-if="promotion" class="p-6 space-y-6">

            <!-- Meta Info -->
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-gray-50 border border-gray-100 rounded-lg">
                <div>
                    <p class="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Section</p>
                    <p class="text-sm font-semibold text-gray-800">{{ promotion.section?.name ?? '—' }}</p>
                </div>
                <div>
                    <p class="text-xs text-gray-400 uppercase tracking-wide mb-0.5">From Session</p>
                    <p class="text-sm font-semibold text-gray-800">{{ promotion.from_session?.name ?? '—' }}</p>
                </div>
                <div>
                    <p class="text-xs text-gray-400 uppercase tracking-wide mb-0.5">To Session</p>
                    <p class="text-sm font-semibold text-gray-800">{{ promotion.to_session?.name ?? '—' }}</p>
                </div>
                <div>
                    <p class="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Promoted By</p>
                    <p class="text-sm font-semibold text-gray-800">{{ promotion.promoted_by?.name ?? '—' }}</p>
                </div>
                <div>
                    <p class="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Date</p>
                    <p class="text-sm font-semibold text-gray-800">{{ formatDate(promotion.created_at) }}</p>
                </div>
            </div>

            <!-- Summary Cards -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div class="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
                    <p class="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Total</p>
                    <p class="text-2xl font-bold text-gray-800">{{ promotion.summary.total }}</p>
                </div>
                <div class="bg-green-50 border border-green-100 rounded-lg px-4 py-3">
                    <p class="text-xs text-green-600 font-medium uppercase tracking-wide mb-1">Promoted</p>
                    <p class="text-2xl font-bold text-green-700">{{ promotion.summary.promoted }}</p>
                </div>
                <div class="bg-orange-50 border border-orange-100 rounded-lg px-4 py-3">
                    <p class="text-xs text-orange-500 font-medium uppercase tracking-wide mb-1">Repeating</p>
                    <p class="text-2xl font-bold text-orange-600">{{ promotion.summary.repeated }}</p>
                </div>
                <div class="bg-primary-50 border border-primary-100 rounded-lg px-4 py-3">
                    <p class="text-xs text-primary font-medium uppercase tracking-wide mb-1">Graduated</p>
                    <p class="text-2xl font-bold text-primary">{{ promotion.summary.graduated }}</p>
                </div>
            </div>

            <!-- Class Room Drawers -->
            <div class="space-y-3">
                <h3 class="text-xs font-bold uppercase tracking-widest text-gray-400">Classes</h3>

                <div v-for="cr in promotion.class_rooms" :key="cr.id"
                    class="border border-gray-200 rounded-lg overflow-hidden">

                    <div class="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition"
                        @click="toggleClassDrawer(cr.id)">

                        <div class="min-w-0 flex-1 pr-4">
                            <p class="text-sm font-semibold text-gray-800">
                                {{ capitalize(cr.from_class?.name) }}
                                <span class="text-gray-400 mx-1">→</span>
                                <span :class="!cr.to_class ? 'text-edit' : ''">
                                    {{ cr.to_class ? capitalize(cr.to_class.name) : 'Graduation' }}
                                </span>
                            </p>
                            <div class="flex flex-wrap items-center gap-1.5 mt-1.5">
                                <span v-if="cr.average_score > 0"
                                    class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                                           bg-gray-100 text-gray-600 rounded-md border border-gray-200">
                                    Average ≥ {{ cr.average_score }}
                                </span>
                                <template v-if="cr.rules_json?.subject_rules?.length">
                                    <span v-for="sr in cr.rules_json.subject_rules" :key="sr.subject_id"
                                        class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                                               bg-primary-50 text-primary rounded-md border border-primary-100">
                                        {{ resolveSubjectName(cr, sr.subject_id) }} ≥ {{ sr.min_score }}
                                    </span>
                                </template>
                                <span v-if="!cr.average_score && !cr.rules_json?.subject_rules?.length"
                                    class="text-xs text-gray-400 italic">
                                    No rules defined
                                </span>
                            </div>
                        </div>

                        <div class="flex items-center gap-3 shrink-0">
                            <div class="hidden md:flex items-center gap-1.5">
                                <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                                             bg-green-50 text-green-700 rounded-md border border-green-100">
                                    {{ cr.summary.promoted }} promoted
                                </span>
                                <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                                             bg-orange-50 text-orange-600 rounded-md border border-orange-100">
                                    {{ cr.summary.repeated }} repeating
                                </span>
                                <span v-if="cr.summary.graduated"
                                    class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                                           bg-primary-50 text-primary rounded-md border border-primary-100">
                                    {{ cr.summary.graduated }} graduated
                                </span>
                            </div>
                            <span class="flex items-center gap-1 text-xs text-gray-400">
                                <PhStudent :size="14" /> {{ cr.students.length }}
                            </span>
                            <PhCaretRight :size="14"
                                class="text-gray-400 transition-transform duration-200"
                                :class="expandedClassId === cr.id ? 'rotate-90' : ''" />
                        </div>
                    </div>

                    <div v-show="expandedClassId === cr.id" class="border-t border-gray-100">
                        <div class="overflow-x-auto">
                            <table class="w-full text-sm">
                                <thead>
                                    <tr class="bg-gray-50 border-b border-gray-100">
                                        <th class="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">#</th>
                                        <th class="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Name</th>
                                        <th class="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">ID</th>
                                        <th class="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">From</th>
                                        <th class="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">To</th>
                                        <th v-if="cr.rules_json?.subject_rules?.length"
                                            class="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                            Subjects
                                        </th>
                                        <th class="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Avg</th>
                                        <th class="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-gray-50">
                                    <tr v-for="(student, i) in cr.students" :key="student.id"
                                        class="hover:bg-gray-50/50 transition">
                                        <td class="px-4 py-2.5 text-gray-400 text-xs">{{ i + 1 }}</td>
                                        <td class="px-4 py-2.5 font-medium text-gray-800 whitespace-nowrap">{{ student.name }}</td>
                                        <td class="px-4 py-2.5 text-gray-500 text-xs font-mono">{{ student.admission_no }}</td>
                                        <td class="px-4 py-2.5 text-gray-600 text-xs whitespace-nowrap">{{ capitalize(student.from_class?.name ?? '—') }}</td>
                                        <td class="px-4 py-2.5 text-gray-600 text-xs whitespace-nowrap">
                                            <span v-if="student.to_class">{{ capitalize(student.to_class.name) }}</span>
                                            <span v-else class="text-edit font-medium">Graduated</span>
                                        </td>
                                        <td v-if="cr.rules_json?.subject_rules?.length" class="px-4 py-2.5">
                                            <div class="flex flex-wrap gap-1">
                                                <span
                                                    v-for="ss in student.subject_scores" :key="ss.name"
                                                    class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md border"
                                                    :class="ss.score === null
                                                        ? 'bg-gray-50 text-gray-400 border-gray-200'
                                                        : ss.passed
                                                            ? 'bg-green-50 text-green-700 border-green-100'
                                                            : 'bg-red-50 text-red-600 border-red-100'"
                                                >
                                                    {{ ss.name }}
                                                    <span class="font-semibold">
                                                        {{ ss.score !== null ? ss.score : '—' }}
                                                    </span>
                                                </span>
                                            </div>
                                        </td>
                                        <td class="px-4 py-2.5 text-left font-semibold text-gray-700">{{ student.average_score }}</td>
                                        <td class="px-4 py-2.5">
                                            <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md border"
                                                :class="statusClass(student.status)">
                                                {{ statusLabel(student.status) }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="!cr.students.length">
                                        <td :colspan="cr.rules_json?.subject_rules?.length ? 8 : 7"
                                            class="px-4 py-6 text-center text-sm text-gray-400">
                                            No students found.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Modal>
</template>